import { useState, useEffect, useRef } from 'react';
import { useHistory, Link, useParams, useLocation } from 'react-router-dom';
import Api from '../api/api';
import stringHelpers from '../../utilities/stringHelpers';
import roleUtilities from '../../utilities/roleUtilities';
import { isDateTimeElapsed, isToDateAfterFromDate } from '../../utilities/dateUtilities';
import Loader from '../shared/loader';
import arrayUtilities from '../../utilities/arrayUtilities';
import { toInputFieldDateTimeString } from '../../utilities/dateUtilities';
import { TimeRangeSlider } from '../shared/timeRangeSlider';
import Pagination from '../shared/pagination';
import { ErrorModal } from '../../utilities/errorModal';
import { Modal } from 'react-bootstrap';
import { SuspensionDisplay } from './suspensionDisplay';
import formUtilities from '../../utilities/formUtilities';
import { SearchBarWithDebounce } from '../shared/searchBarWithDebounce';
import { FiltersModalTrigger } from '../shared/filtersModalTrigger';
import { AppliedFilterDisplay } from '../shared/appliedFilterDisplay';
import { EnforcementSuspensionAddEditFiltersModal } from './enforcementSuspensionAddEditFiltersModal';

export const EnforcementSuspensionAddEdit = ({ user, sites, clients, organisationId, token }) => {
    const { suspensionId } = useParams()
    const history = useHistory()
    const location = useLocation()
    const returnUrl = `/organisation/${ organisationId }/enforcement-suspensions`
    const isNew = stringHelpers.isNullOrEmpty(suspensionId)
    const [isInEdit, setIsInEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false)
    const [suspension, setSuspension] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [name, setName] = useState("")
    const [searchQuery, setSearchQuery] = useState("")
    const [selectedSites, setSelectedSites] = useState([]);
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false)
    const [isLoadingSites, setIsLoadingSites] = useState(false)
    const [loadingSiteIds, setLoadingSiteIds] = useState([])
    const [selectedClient, setSelectedClient] = useState("");
    const [sitesByClient, setSitesByClient] = useState({ items: [], totalCount: 0 })
    const [sitesPageNumber, setSitesPageNumber] = useState(0)
    const [showSitesFiltersModal, setShowSitesFiltersModal] = useState(false)
    const [showSitesFilterPills, setShowSitesFilterPills] = useState(false)
    const [sitesAppliedFilters, setSitesAppliedFilters] = useState(null)
    const [isEnabled, setIsEnabled] = useState(false)
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [validOnlyOnSpecificDays, setValidOnlyOnSpecificDays] = useState(false)
    const [validityDays, setValidityDays] = useState([
        {
            label: "Monday",
            isEnabled: false,
        },
        {
            label: "Tuesday",
            isEnabled: false,
        },
        {
            label: "Wednesday",
            isEnabled: false,
        },
        {
            label: "Thursday",
            isEnabled: false,
        },
        {
            label: "Friday",
            isEnabled: false,
        },
        {
            label: "Saturday",
            isEnabled: false,
        },
        {
            label: "Sunday",
            isEnabled: false,
        }
    ])

    const [validOnlyAtSpecificTimes, setValidOnlyAtSpecificTimes] = useState(false)
    const [enabledTimes, setEnabledTimes] = useState({
        from: "00:00:00",
        to: "23:59:00"
    })

    const [notes, setNotes] = useState("")
    const [errorMessage, setErrorMessage] = useState(null)
    const [sitesUpdateError, setSitesUpdateError] = useState(false)
    const [exitPrompt, setExitPrompt] = useState(null)
    const getSuspension = () => {
        if (location && location.state !== undefined && location.state.error !== undefined) {
            setSitesUpdateError(location.state.error)
        }
        Api.getSuspension(
            data => {
                setSuspension({
                    ...data,
                    startDate: data.startDate,
                    endDate: data.endDate,
                })
                setName(data.name)
                setIsEnabled(data.isEnabled)
                setSelectedSites(data.suspensionSites.map(item => item.siteId))
                setStartDate(!stringHelpers.isNullOrEmpty(data.startDate) ? data.startDate : "")
                setEndDate(!stringHelpers.isNullOrEmpty(data.endDate) ? data.endDate : "")
                const days = validityDays.map(
                    day => ({
                        ...day,
                        isEnabled: data[day.label.toLowerCase()],
                    })
                )
                if (days.find(item => item.isEnabled) !== undefined) {
                    setValidOnlyOnSpecificDays(true)
                }
                setValidityDays(days)
                if (stringHelpers.isNullOrEmpty(data.enabledFromTime) || stringHelpers.isNullOrEmpty(data.enabledToTime)) {
                    setValidOnlyAtSpecificTimes(false)
                } else {
                    setValidOnlyAtSpecificTimes(true)
                    setEnabledTimes({
                        from: data.enabledFromTime,
                        to: data.enabledToTime
                    })
                }
                setNotes(data.notes)
                setIsLoading(false)
            },
            error => {
                console.log(error)
                setIsLoading(false)
            },
            suspensionId,
            organisationId, token
        )
    }

    const submit = () => {
        setIsLoading(true)
        if (!isNew) {
            Api.editSuspension(
                data => {
                    if (data.isError) {
                        setErrorMessage(data.errorMessage)
                    } else {
                        setIsInEdit(false)
                        getSuspension()
                    }
                    setIsLoading(false)
                },
                error => {
                    console.log(error)
                    setErrorMessage(error.errorMessage)
                    setIsLoading(false)
                },
                suspensionId,
                name,
                new Date(startDate).toISOString(),
                new Date(endDate).toISOString(),
                ...validityDays.map(item => item.isEnabled),
                validOnlyAtSpecificTimes ? enabledTimes.from : null,
                validOnlyAtSpecificTimes ? enabledTimes.to : null,
                notes,
                isEnabled,
                organisationId,
                token
            )
        } else {
            Api.addSuspension(
                data => {
                    if (data.isError) {
                        setErrorMessage(data.errorMessage)
                        setIsLoading(false)
                    } else {
                        const navigateTo = (hasError) => history.push(
                            `/organisation/${ organisationId }/enforcement-suspension/${ data.id }`,
                            hasError ? { error: true } : undefined
                        )
                        if (!arrayUtilities.isNullOrEmpty(selectedSites)) {
                            Api.enableSuspensionSites(
                                sitesResult => navigateTo(sitesResult.isError),
                                () => navigateTo(true),
                                data.id, selectedSites,
                                organisationId, token
                            )
                        } else {
                            navigateTo(null)
                        }
                    }
                },
                error => {
                    console.log(error)
                    setErrorMessage(error.errorMessage)
                    setIsLoading(false)
                },
                name,
                new Date(startDate).toISOString(),
                new Date(endDate).toISOString(),
                ...validityDays.map(item => item.isEnabled),
                validOnlyAtSpecificTimes ? enabledTimes.from : null,
                validOnlyAtSpecificTimes ? enabledTimes.to : null,
                notes,
                isEnabled,
                organisationId,
                token
            )
        }

    }

    const sitesAbortController = useRef(null);

    const getSites = (query, clientId, page) => {
        setIsLoadingSites(true)
        if (sitesAbortController.current) {
            sitesAbortController.current.abort();
        }
        sitesAbortController.current = new AbortController();
        const signal = sitesAbortController.current.signal;
        Api.searchSites(
            (data) => {
                setSitesByClient({ items: data.items, totalCount: data.totalCount });
                setIsLoadingSites(false)
            },
            (error) => {
                console.log(error)
                setSitesByClient({ items: [], totalCount: 0 });
                setIsLoadingSites(false)
            },
            query,
            page,
            !stringHelpers.isNullOrEmpty(clientId) ? clientId : null,
            null,
            null,
            null,
            organisationId,
            token,
            signal
        );
    };
    useEffect(() => {
        getSites("", null, 0)
        if (isNew) {
            setIsInEdit(true)
        } else {
            setIsInEdit(false)
            setIsLoading(true)
            getSuspension()
        }
    }, [location])

    const handleSelectAllAttachDetach = (event) => {
        const isChecked = event.target.checked
        const siteIds = sitesByClient.items.map(item => item.id)

        setIsSelectAllChecked(isChecked)

        if (isNew) {
            if (isChecked) {
                setSelectedSites(prevState => [...new Set([...prevState, ...siteIds])])
            } else {
                setSelectedSites(prevState => prevState.filter(id => !siteIds.includes(id)))
            }
            return
        }

        setLoadingSiteIds(prevState => [...prevState, ...siteIds])

        if (isChecked) {
            setSelectedSites(prevState => [...new Set([...prevState, ...siteIds])])
            Api.enableSuspensionSites(
                sitesResult => {
                    if (sitesResult.isError) {
                        setErrorMessage(sitesResult.errorMessage)
                        setSelectedSites(prevState => prevState.filter(id => !siteIds.includes(id)))
                        setIsSelectAllChecked(false)
                    }
                    setLoadingSiteIds(prevState => prevState.filter(id => !siteIds.includes(id)))
                },
                error => {
                    console.log(error)
                    setErrorMessage(error.errorMessage)
                    setSelectedSites(prevState => prevState.filter(id => !siteIds.includes(id)))
                    setIsSelectAllChecked(false)
                    setLoadingSiteIds(prevState => prevState.filter(id => !siteIds.includes(id)))
                },
                suspensionId, siteIds,
                organisationId, token
            )
        } else {
            setSelectedSites(prevState => prevState.filter(id => !siteIds.includes(id)))
            Api.detachSuspensionSites(
                sitesResult => {
                    if (sitesResult.isError) {
                        setErrorMessage(sitesResult.errorMessage)
                        setSelectedSites(prevState => [...new Set([...prevState, ...siteIds])])
                        setIsSelectAllChecked(true)
                    }
                    setLoadingSiteIds(prevState => prevState.filter(id => !siteIds.includes(id)))
                },
                error => {
                    console.log(error)
                    setErrorMessage(error.errorMessage)
                    setSelectedSites(prevState => [...new Set([...prevState, ...siteIds])])
                    setIsSelectAllChecked(true)
                    setLoadingSiteIds(prevState => prevState.filter(id => !siteIds.includes(id)))
                },
                suspensionId, siteIds,
                organisationId, token
            )
        }
    }

    const handleSiteAttachDetach = (site) => {
        const isChecked = !selectedSites.includes(site.id)

        if (isChecked) {
            setSelectedSites(prevState => [...prevState, site.id])
            if (!arrayUtilities.isNullOrEmpty(sitesByClient.items) && sitesByClient.items.length - selectedSites.length === 1) {
                setIsSelectAllChecked(true)
            }
        } else {
            setSelectedSites(prevState => prevState.filter(id => id !== site.id))
            if (isSelectAllChecked) {
                setIsSelectAllChecked(false)
            }
        }

        if (isNew) return

        setLoadingSiteIds(prevState => [...prevState, site.id])

        if (isChecked) {
            Api.enableSuspensionSites(
                sitesResult => {
                    if (sitesResult.isError) {
                        setErrorMessage(sitesResult.errorMessage)
                        setSelectedSites(prevState => prevState.filter(id => id !== site.id))
                    }
                    setLoadingSiteIds(prevState => prevState.filter(id => id !== site.id))
                },
                error => {
                    console.log(error)
                    setErrorMessage(error.errorMessage)
                    setSelectedSites(prevState => prevState.filter(id => id !== site.id))
                    setLoadingSiteIds(prevState => prevState.filter(id => id !== site.id))
                },
                suspensionId, [site.id],
                organisationId, token
            )
        } else {
            Api.detachSuspensionSites(
                sitesResult => {
                    if (sitesResult.isError) {
                        setErrorMessage(sitesResult.errorMessage)
                        setSelectedSites(prevState => [...prevState, site.id])
                    }
                    setLoadingSiteIds(prevState => prevState.filter(id => id !== site.id))
                },
                error => {
                    console.log(error)
                    setErrorMessage(error.errorMessage)
                    setSelectedSites(prevState => [...prevState, site.id])
                    setLoadingSiteIds(prevState => prevState.filter(id => id !== site.id))
                },
                suspensionId, [site.id],
                organisationId, token
            )
        }
    }

    const handleDelete = () => {
        Api.deleteSuspension(
            data => {
                if (data.isError) {
                    setErrorMessage(data.errorMessage)
                } else {
                    history.push(returnUrl)
                }
            },
            error => {
                console.log(error)
                setErrorMessage(error.message)
            },
            suspensionId,
            organisationId, token
        )
    }

    return (
        <div className="container-fluid mandatory-date-time">
            <div className="row">
                <div className="col-12">
                    {
                        !isNew && suspension &&
                        <div className="card mb-3">
                            <div className="card-header">
                                <div className="row align-items-center">
                                    <div className="col">
                                        <h5 className="mb-0 d-flex align-items-center">Suspension - <p className="ml-2 mb-0">{ suspension.name }</p></h5>
                                    </div>
                                    <button 
                                        className="btn btn-falcon-default btn-sm mr-3 py-1" 
                                        type="button"
                                        onClick={() => {
                                            if (!isEnabled) {
                                                setExitPrompt({
                                                    body: "Are you sure you want to leave this page without enabling this enforcement suspension?",
                                                    confirmButton: {
                                                        text: "Leave",
                                                        action: () => history.push(returnUrl)
                                                    },
                                                    cancelButtonText: "Stay on page",
                                                })
                                            } else {
                                                history.push(returnUrl)
                                            }
                                        }}
                                    ><i className="fas fa-reply"></i> Back</button>
                                    {
                                        (
                                            roleUtilities.isOrganisationAdminOrManager(user, organisationId) ||
                                            roleUtilities.isClientManager(user, organisationId) ||
                                            (
                                                roleUtilities.isClient(user, organisationId) &&
                                                !stringHelpers.isNullOrEmpty(suspension.endDate) && isDateTimeElapsed(suspension.endDate)
                                            )
                                        ) &&
                                        <button className="btn btn-falcon-default btn-sm dropdown-toggle py-2 mr-3 dropdown-caret-none" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="true"><i className="fas fa-ellipsis-h"></i></button>
                                    }
                                    <div className="dropdown-menu dropdown-menu-right" x-placement="bottom-start">
                                        <button onClick={ () => setShowDeleteModal(true) } className="dropdown-item text-danger">
                                            <i className="fas fa-times"></i> Delete Suspension</button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    }

                    <div className="card mb-3">
                        <div className="card-header">
                            <div className="row align-items-center">
                                <div className="col">
                                    <h5 className="mb-0">
                                        { isNew ? <>Add new Suspension</> : <>Details</> }
                                    </h5>
                                </div>
                                {
                                    !isNew && roleUtilities.isOrganisationAdminOrManager(user, organisationId) &&
                                    <div className="col-auto">
                                        <button type="button" className="btn btn-primary btn-sm ml-2" onClick={ () => setIsInEdit(!isInEdit) }>
                                            <i className="fas fa-edit"></i> Edit
                                        </button>
                                    </div>
                                }
                            </div>
                        </div>
                        {
                            isLoading
                                ? <Loader />
                                : isInEdit
                                    ? <>
                                        <div className="card-body bg-light border-top details-container">
                                            <div className="row mb-1">
                                                <div className="col-md-3 col-12 ">
                                                    <p className="font-weight-semi-bold  mt-1 mb-1">Name<span className="req"></span></p>
                                                </div>
                                                <div className="col-md-6 col-12">
                                                    <div className="form-group mb-0">
                                                        <input required className="form-control mb-0" type="text" value={ name } onChange={ e => {
                                                            setName(e.target.value)
                                                            formUtilities.requiredField(e.target.parentNode, e.target.value)
                                                        } } />
                                                        <small className='invalid-feedback text-danger'>Please enter a name</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row mb-1">
                                                <div className="col-md-3 col-12 ">
                                                    <p className="font-weight-semi-bold  mt-1 mb-1">Enabled</p>
                                                </div>
                                                <div className="col-md-6 col-12 px-0 pt-1">
                                                    <div className='custom-control custom-switch'>
                                                        <input className="custom-control-input" type="checkbox" id="suspension-enabled-toggle" checked={ isEnabled } onChange={ (e) => setIsEnabled(!isEnabled) } />
                                                        <label className="custom-control-label custom-toggle ml-3" htmlFor="suspension-enabled-toggle"></label>
                                                    </div>
                                                </div>
                                            </div>
                                            {
                                                
                                                <div className="row mt-2">
                                                    <div className="col-md-3 col-12 ">
                                                        <p className={ `font-weight-semi-bold ${ isInEdit ? "" : "mb-1" }` }>Sites</p>
                                                        {!isNew && <small className='m-0'>Select sites to associate with this suspension</small>}
                                                    </div>
                                                    <div className="col-md-6 col-12 d-flex align-items-start">
                                                        {
                                                            sitesByClient && 
                                                            <div className='w-100'>
                                                                <div className="d-flex align-items-center mb-2">
                                                                    <SearchBarWithDebounce onSearchQueryChange={ query => {
                                                                        setSearchQuery(query)
                                                                        setSitesPageNumber(0)
                                                                        getSites(query, selectedClient, 0)
                                                                    } } />
                                                                    <div className='ml-2'>
                                                                        <FiltersModalTrigger onClick={ () => setShowSitesFiltersModal(true) } />
                                                                    </div>
                                                                </div>
                                                                {
                                                                    showSitesFilterPills && sitesAppliedFilters &&
                                                                    <div className="d-flex align-items-center flex-wrap mb-2">
                                                                        {
                                                                            Object.entries(sitesAppliedFilters).map(
                                                                                ([label, value]) => !stringHelpers.isNullOrEmpty(value)
                                                                                && <div className="mr-2" key={ label }>
                                                                                    <AppliedFilterDisplay
                                                                                        label={ label }
                                                                                        onTextClick={ () => setShowSitesFiltersModal(true) }
                                                                                        onCloseClick={ closedLabel => {
                                                                                            setSelectedClient("")
                                                                                            setSitesPageNumber(0)
                                                                                            getSites(searchQuery, "", 0)
                                                                                            setSitesAppliedFilters(prevState => {
                                                                                                const updatedFilters = { ...prevState }
                                                                                                updatedFilters[closedLabel] = null
                                                                                                const hasRemainingFilters = Object.values(updatedFilters).some(value => !stringHelpers.isNullOrEmpty(value))
                                                                                                return hasRemainingFilters ? updatedFilters : null
                                                                                            })
                                                                                        } }
                                                                                        value={ value }
                                                                                    />
                                                                                </div>
                                                                            )
                                                                        }
                                                                        { Object.keys(sitesAppliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => {
                                                                            setSelectedClient("")
                                                                            setSitesPageNumber(0)
                                                                            setSitesAppliedFilters(null)
                                                                            getSites(searchQuery, "", 0)
                                                                        } }>Clear all filters</button> }
                                                                    </div>
                                                                }
                                                                <div style={ { height: "250px", overflowY: "auto" } }>
                                                                    <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table w-100 border-bottom border-200 no-footer">
                                                                        <thead className="bg-200 text-900" style={ { position: "sticky", top: 0, zIndex: 1 } }>
                                                                            <tr>
                                                                                <td>
                                                                                    <input className="" type="checkbox" checked={ isSelectAllChecked } id="select-all-sites" onChange={ handleSelectAllAttachDetach } />
                                                                                </td>
                                                                                <th>Client</th>
                                                                                <th>Name</th>
                                                                                <th>Code</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {
                                                                                isLoadingSites ? <tr><td className='text-center' colSpan={ 5 }><Loader /></td></tr>
                                                                                    : !arrayUtilities.isNullOrEmpty(sitesByClient.items)
                                                                                    && sitesByClient.items.map(
                                                                                        site => <tr key={ site.id } className="cursor-pointer" style={ loadingSiteIds.includes(site.id) ? { opacity: 0.5, pointerEvents: 'none' } : {} } onClick={ () => handleSiteAttachDetach(site) }>
                                                                                            <td>
                                                                                                {
                                                                                                    loadingSiteIds.includes(site.id)
                                                                                                        ? <span className="tiny-loader checkbox-loader"><Loader /></span>
                                                                                                        : <input type="checkbox" checked={ selectedSites?.includes(site.id) } id={ `select-site-${ site.id }` } onChange={ () => {} } />
                                                                                                }
                                                                                            </td>
                                                                                            <td>{ site.clientName }</td>
                                                                                            <td><h6 className="mb-0">{ site.name }</h6></td>
                                                                                            <td>{ site.siteCode }</td>
                                                                                        </tr>
                                                                                    )
                                                                            }
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                                <div className="d-flex justify-content-center row align-items-center pt-2">
                                                                    <div className="col-auto align-items-center">
                                                                        <Pagination
                                                                            totalResults={ sitesByClient.totalCount }
                                                                            currentPage={ sitesPageNumber }
                                                                            changePageNumber={ number => {
                                                                                getSites(searchQuery, selectedClient, number)
                                                                                setSitesPageNumber(number)
                                                                            } } />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            }
                                            <hr className="border-dashed border-bottom-0"></hr>
                                            <h6 className="mb-3 fs-1">Date/Time Details</h6>
                                            <div className="row mb-3">
                                                <div className="col-md-3 col-12 ">
                                                    <p className="font-weight-semi-bold  mt-1 mb-1">Start Date<span className="req"></span></p>
                                                </div>
                                                <div className="col-md-6 col-12">
                                                    <div className="form-group mb-0">
                                                        <input required className="form-control mb-1" type="datetime-local" value={ toInputFieldDateTimeString(startDate) } onChange={ (e) => {
                                                            setStartDate(e.target.value)
                                                            formUtilities.requiredField(e.target.parentNode, e.target.value)
                                                        } } />
                                                        <small className='invalid-feedback text-danger'>Please enter a start date</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row mb-3">
                                                <div className="col-md-3 col-12 ">
                                                    <p className="font-weight-semi-bold  mt-1 mb-1">End Date<span className="req"></span></p>
                                                </div>
                                                <div className="col-md-6 col-12">
                                                    <div className="form-group mb-0">
                                                        <input required className="form-control mb-1" type="datetime-local" value={ toInputFieldDateTimeString(endDate) } onChange={ (e) => {
                                                            setEndDate(e.target.value)
                                                            formUtilities.requiredField(e.target.parentNode, e.target.value)
                                                        } } />
                                                        <small className='invalid-feedback text-danger'>Please enter an end date</small>
                                                    </div>
                                                    {!isToDateAfterFromDate(startDate, endDate) && <small className='fs--1 text-danger'>End date cannot be before start date&nbsp;<i className='fas fa-exclamation-circle'></i></small>}
                                                </div>
                                            </div>

                                            <div className={ `row mt-3 ${ validOnlyOnSpecificDays ? "mb-3" : "" }` }>
                                                <div className="col-md-3 col-12 ">
                                                    <p className="font-weight-semi-bold mb-1">Suspend only on specific days</p>
                                                </div>
                                                <div className="col-md-6 col-12 custom-control custom-switch">
                                                    <input className="custom-control-input" type="checkbox" id="enable-any-day-week-switch" checked={ validOnlyOnSpecificDays } onChange={ e => {
                                                        setValidOnlyOnSpecificDays(!validOnlyOnSpecificDays)
                                                        if (!e.target.checked) {
                                                            setValidityDays(prevState => (prevState.map(
                                                                item => ({
                                                                    ...item,
                                                                    isEnabled: false
                                                                })
                                                            )))
                                                        }
                                                    } } />
                                                    <label className="custom-control-label ml-3" htmlFor="enable-any-day-week-switch"></label>
                                                    <p className="m-0 ml-3 mt-1">{ validOnlyOnSpecificDays ? <>Yes</> : <>No</> }</p>
                                                </div>
                                            </div>

                                            <div className="mt-3">
                                                {
                                                    validOnlyOnSpecificDays &&
                                                    validityDays.map(
                                                        (day, index) => <div key={ index } className="row mb-3">
                                                            <div className="col-md-3 col-12 ">
                                                                <p className="font-weight-semi-bold  mt-1 mb-1">{ day.label }</p>
                                                            </div>
                                                            <div className="col-md-6 col-12 custom-control custom-switch">
                                                                <input className="custom-control-input" type="checkbox" id={ `enable-${ day.label }-switch` } checked={ day.isEnabled }
                                                                    onChange={ e => setValidityDays(prevState => {
                                                                        const newArray = prevState.toSpliced(index, 1, {
                                                                            ...prevState[index],
                                                                            isEnabled: e.target.checked
                                                                        })
                                                                        return newArray
                                                                    }) }
                                                                />
                                                                <label className="custom-control-label ml-3" htmlFor={ `enable-${ day.label }-switch` }></label>
                                                                <p className="m-0 ml-3 mt-1">{ day.isEnabled ? <>Yes</> : <>No</> }</p>
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </div>

                                            <div className={ `row mt-3 ${ validOnlyAtSpecificTimes ? "mb-3" : "" }` }>
                                                <div className="col-md-3 col-12 ">
                                                    <p className="font-weight-semi-bold mb-1 ">Suspend only at specific times of the day</p>
                                                </div>
                                                <div className="col-md-6 col-12 custom-control custom-switch">
                                                    <input className="custom-control-input" type="checkbox" id="enable-any-time-day-switch" checked={ validOnlyAtSpecificTimes } onChange={ e => {
                                                        if (e.target.checked) {
                                                            setValidOnlyAtSpecificTimes(true)
                                                        } else {
                                                            setValidOnlyAtSpecificTimes(false)
                                                            setEnabledTimes({ from: "00:00:00", to: "23:59:59" })
                                                        }
                                                    } } />
                                                    <label className="custom-control-label ml-3" htmlFor="enable-any-time-day-switch"></label>
                                                    <p className="m-0 ml-3 mt-1">{ validOnlyAtSpecificTimes ? <>Yes</> : <>No</> }</p>
                                                </div>
                                            </div>
                                            {
                                                validOnlyAtSpecificTimes &&
                                                <div className="row mb-3">
                                                    <div className="col-md-3 col-12 ">
                                                        <p className="font-weight-semi-bold mb-1">Time Range</p>
                                                    </div>
                                                    <div className="col-md-6 col-12">
                                                        <TimeRangeSlider
                                                            use24HourClock
                                                            startTime={ enabledTimes.from }
                                                            endTime={ enabledTimes.to }
                                                            onChange={ value => {
                                                                const withSeconds = (time) => time.split(':').length === 2 ? `${ time }:00` : time;
                                                                setEnabledTimes({
                                                                    from: withSeconds(value.startTime),
                                                                    to: withSeconds(value.endTime)
                                                                })
                                                            } }
                                                        />
                                                    </div>
                                                </div>
                                            }
                                            <hr className="border-dashed border-bottom-0"></hr>
                                            <div className="row">
                                                <div className="col-md-3 col-12 ">
                                                    <p className="font-weight-semi-bold mt-1 mb-1">Notes</p>
                                                </div>
                                                <div className="col-md-6 col-12">
                                                    <div className="form-group mb-1">
                                                        <textarea className="form-control mb-0" rows={ 4 } value={ notes } onChange={ (e) => setNotes(e.target.value) } />
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                        <div className="card-footer border-top ">
                                            <div className="row client-edit-buttons">
                                                <div className="col-12 col-sm-auto ml-auto pl-0">
                                                    <div id="dashboard-actions">
                                                        <button type="button" className="btn btn-falcon-default btn-sm ml-2"
                                                            onClick={ () => {
                                                                if (isNew) {
                                                                    history.push(returnUrl)
                                                                } else {
                                                                    setIsInEdit(false)
                                                                }
                                                            } }>
                                                            <i className="fas fa-times mr-1"></i>
                                                            Cancel
                                                        </button>
                                                        <button
                                                            disabled={ stringHelpers.isNullOrEmpty(name) || stringHelpers.isNullOrEmpty(startDate) || stringHelpers.isNullOrEmpty(endDate) || !isToDateAfterFromDate(startDate, endDate) }
                                                            type="button" className="btn btn-success btn-sm ml-2" onClick={ () => {
                                                                if (!isEnabled) {
                                                                    setExitPrompt({
                                                                        body: "Are you sure you want to save without enabling this enforcement suspension?",
                                                                        confirmButton: {
                                                                            text: "Save without enabling",
                                                                            action: () => submit()
                                                                        },
                                                                        cancelButtonText: "Continue editing",
                                                                    })
                                                                } else {
                                                                    submit()
                                                                }
                                                            } }>
                                                            <i className="fas fa-check mr-1"></i>
                                                            Confirm
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                    : suspension && <SuspensionDisplay sitesUpdateError={ sitesUpdateError } validityDays={ validityDays } organisationId={ organisationId } suspension={ suspension } sites={ sites } />
                        }

                    </div>
                </div>
            </div>
            { !stringHelpers.isNullOrEmpty(errorMessage) && <ErrorModal message={ errorMessage } onHide={ () => setErrorMessage(null) } /> }
            {
                showDeleteModal && <Modal show onHide={ () => setShowDeleteModal(false) } centered>
                    <Modal.Header>
                        <Modal.Title className="d-flex gap-2 align-items-center text-danger">Delete Suspension</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="font-weight-semi-bold mt-1 mb-2">Deleting <b>{ name }</b> will affect { suspension.suspensionSites.length } site{ suspension.suspensionSites.length !== 1 && <>s</> }. Are you sure you want to proceed?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-falcon-default btn-sm ml-2" onClick={ () => setShowDeleteModal(false) }>Cancel</button>
                        <button type="button" className="btn btn-danger btn-sm ml-2" onClick={ () => {
                            setShowDeleteModal(false)
                            handleDelete()
                        } }>Delete</button>
                    </Modal.Footer>
                </Modal>
            }

            {
                exitPrompt !== null && <Modal show onHide={ () => setExitPrompt(null) } centered>
                    <Modal.Header>
                        <Modal.Title className="d-flex gap-2 align-items-center text-warning">Warning</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="font-weight-semi-bold mt-1 mb-2">{exitPrompt.body}</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-falcon-default btn-sm ml-2" onClick={ () => {
                            setExitPrompt(null)
                            exitPrompt.confirmButton.action()
                        } }>{exitPrompt.confirmButton.text}</button>
                        <button type="button" className="btn btn-primary btn-sm ml-2" onClick={ () => setExitPrompt(null) }>
                            {exitPrompt.cancelButtonText}
                        </button>
                    </Modal.Footer>
                </Modal>
            }
            {
                showSitesFiltersModal &&
                <EnforcementSuspensionAddEditFiltersModal
                    showModal={ showSitesFiltersModal }
                    handleClose={ () => setShowSitesFiltersModal(false) }
                    handleConfirm={ (draftFilters) => {
                        setSelectedClient(draftFilters.selectedClient)
                        setSitesAppliedFilters(draftFilters.appliedFilters)
                        setShowSitesFilterPills(true)
                        setShowSitesFiltersModal(false)
                        setSitesPageNumber(0)
                        getSites(searchQuery, draftFilters.selectedClient, 0)
                    } }
                    initialSelectedClient={ selectedClient }
                    initialAppliedFilters={ sitesAppliedFilters }
                    clients={ clients }
                    key="enforcement-suspension-add-edit-filters-modal"
                />
            }
        </div>
    )
}